import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Filters, Pagination } from './uiChrome.jsx';
import { AlertTriangle } from 'lucide-react';
import MarketplaceCard from './MarketplaceCard.jsx';
import MarketplaceFiltersSlideout from './MarketplaceFiltersSlideout.jsx';
import MarketplaceEmptyState from './MarketplaceEmptyState.jsx';
import { useMarketplace } from '../hooks/marketplace';

const SORT_OPTIONS = ['az', 'newest', 'popular'];
const CARD_GRID_CLASS = 'grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3';

/**
 * Browse All (CT-4166): the full catalog with a sticky toolbar (search + sort +
 * All Filters), a grid of `MarketplaceCard`, and pagination.
 *
 * Toolbar is `@aimsos/ui`'s `Filters` (per the ticket's component mapping):
 * `searchProps` owns search, `showAllFilters` gives the always-visible "All
 * Filters" entry point (mobile compact AND desktop), and `sort` drives the
 * field + asc/desc-direction control (`FilterSortControls`). The AC's three
 * named presets (Alphabetical A–Z / Newest first / Most popular) are the sort
 * *fields*; each has its own natural default direction (`useMarketplace`'s
 * `SORT_FIELD_DEFAULT_DIRECTION`), and the direction toggle reverses whichever
 * field is active (e.g. "Newest first" reversed reads as oldest-first) instead
 * of being a dead control.
 *
 * The active-filter count badge (AC: "Badge on the All Filters Button") still
 * isn't rendered: `Filters`' built-in All-Filters button has no slot to inject
 * a badge into. `activeFilterCount` is real now (CT-4168's slideout drives
 * `setFilters`) — the gap is purely that `Filters` needs a badge slot.
 *
 * The toolbar uses CSS `sticky top-0` so it sticks once the user scrolls past
 * the curated sections and releases on scroll-up — no fixed offsets. The Filters
 * Slideout (CT-4168) mounts below, controlled by `filtersOpen` and wired to
 * `filters`/`setFilters`. Zero matches renders `MarketplaceEmptyState` (CT-4169)
 * in place of the grid — its "Clear filters" escape route is `clearAll`.
 */
const MarketplaceBrowseAll = () => {
  const { t } = useTranslation('admin-studio');
  const navigate = useNavigate();
  const {
    items,
    total,
    isLoading,
    error,
    search,
    setSearch,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    filters,
    setFilters,
    clearAll,
    page,
    setPage,
    perPage,
    totalPages,
  } = useMarketplace();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const sortOptions = useMemo(
    () =>
      SORT_OPTIONS.map((opt) => ({
        value: opt,
        label: t(`integrations.marketplace.browseAll.sort.${opt}`),
      })),
    [t],
  );

  // Stable handler so memoized MarketplaceCard instances aren't re-rendered on
  // every keystroke/sort/page change (same pattern as MarketplaceTab.jsx).
  const openProvider = useCallback((slug) => navigate(slug), [navigate]);

  // `Filters` is memo()'d — these must stay referentially stable across
  // renders (e.g. every search keystroke) or the memo never skips a render,
  // and Filters' own internal useMemos keyed on `sort` recompute for nothing.
  const searchProps = useMemo(
    () => ({
      value: search,
      onChange: (e) => setSearch(e?.target ? e.target.value : e),
      onClear: () => setSearch(''),
      placeholder: t('integrations.marketplace.browseAll.searchPlaceholder'),
    }),
    [search, setSearch, t],
  );

  const sortConfig = useMemo(
    () => ({
      value: sortBy,
      defaultValue: 'az',
      options: sortOptions,
      onChange: setSortBy,
      direction: sortDirection,
      defaultDirection: 'asc',
      onDirectionChange: setSortDirection,
    }),
    [sortBy, sortOptions, setSortBy, sortDirection, setSortDirection],
  );

  const handleAllFiltersClick = useCallback(() => setFiltersOpen(true), []);
  const closeFilters = useCallback(() => setFiltersOpen(false), []);

  const renderResults = () => {
    if (error) {
      return <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />;
    }
    if (isLoading) {
      return (
        <div className={CARD_GRID_CLASS}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      );
    }
    if (total === 0) {
      return <MarketplaceEmptyState query={search.trim()} onClearFilters={clearAll} />;
    }
    return (
      <>
        <div className={CARD_GRID_CLASS}>
          {items.map((view) => (
            <MarketplaceCard key={view.id} view={view} onOpen={openProvider} />
          ))}
        </div>
        <Pagination
          rangeLabel={
            total === 0 ? '0–0' : `${(page - 1) * perPage + 1}–${Math.min(page * perPage, total)}`
          }
          totalItems={total}
          currentPage={page}
          totalPages={totalPages}
          perPage={perPage}
          hasPrevious={page > 1}
          hasNext={page < totalPages}
          onPrevPage={() => setPage(Math.max(1, page - 1))}
          onNextPage={() => setPage(Math.min(totalPages, page + 1))}
        />
      </>
    );
  };

  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-sm font-semibold text-content">{t('integrations.marketplace.browseAll.title')}</h3>

      <div className="sticky top-0 z-10 bg-surface-base/95 py-2 backdrop-blur">
        <Filters
          searchProps={searchProps}
          maxFilters={4}
          showAllFilters
          onAllFiltersClick={handleAllFiltersClick}
          sort={sortConfig}
        />
      </div>

      {renderResults()}

      <MarketplaceFiltersSlideout
        isOpen={filtersOpen}
        onClose={closeFilters}
        appliedFilters={filters}
        onApply={setFilters}
      />
    </section>
  );
};

export default MarketplaceBrowseAll;
